import React, { useEffect, useState } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { MultiSelectType } from '../../../types/common.types';
import { makeStyles } from '@mui/styles';
import { colors, theme } from '../../../theme';
import { Autocomplete, Checkbox, ListItemText, Select, SelectChangeEvent, TextField } from '@mui/material';
import { ToolTip } from '../tooltip/tooltip';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

export const testid = 'multi-select';

const color = colors();
const themes = theme().typography;
export const MultiSelectDropDown = ({
	label,
	width = 150,
	data,
	value,
	showValue,
	hasTooltip,
	hasSearch,
	onSelectCallback,
}: MultiSelectType) => {
	const classes = useStyles();
	const sortedData = data?.sort();
	const [selValue, setSelValue] = useState<string[]>(value);
	const [selectedOption, setSelectedOption] = useState(value);

	const MenuProps = {
		PaperProps: {
			style: {
				maxHeight: 48 * 4.5 + 8,
				width: 250,
			},
		},
	};

	useEffect(() => {
		setSelValue(value);
		setSelectedOption(value);
	}, [value]);

	const handleChange = (event: SelectChangeEvent<typeof selValue>) => {
		const {
			target: { value },
		} = event;
		let data = typeof value === 'string' ? value.split(',') : value;
		setSelValue(data);
		onSelectCallback && onSelectCallback(data);
	};

	const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
	const checkedIcon = <CheckBoxIcon fontSize="small" />;

	return (
		<FormControl sx={{ m: 0.5, width: { width } }} size="small" className={classes.dropdown} data-testid={testid}>
			{hasSearch ? (
				<Autocomplete
					multiple
					disableCloseOnSelect
					options={sortedData || []}
					getOptionLabel={(option) => option}
					value={selectedOption}
					onChange={(event, newValue) => {
						setSelectedOption(newValue);
						onSelectCallback && onSelectCallback(newValue);
					}}
					renderOption={(props, option, { selected }) => (
						<li {...props} style={{ padding: '0px' }}>
							<Checkbox
								icon={icon}
								checkedIcon={checkedIcon}
								style={{ color: color.primary[90] }}
								checked={selected}
								size="small"
							/>
							{option}
						</li>
					)}
					renderTags={() => `(${selectedOption.length}) selected`}
					renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={label} />}
					ListboxProps={{
						style: {
							maxHeight: 220,
							overflowY: 'auto',
							fontSize: '12px',
							padding: '0px',
						},
					}}
					slotProps={{
						popper: {
							sx: {
								width: '250px !important',
							},
						},
					}}
				/>
			) : (
				<>
					<InputLabel id={`multi-input-${label}`} data-testid={`${testid}-label`}>
						{label}
					</InputLabel>
					<Select
						data-testid={`${testid}-dropdown`}
						labelId={`multi-${label}`}
						id={`multi-${label}`}
						multiple
						value={selValue}
						onChange={handleChange}
						label={label}
						MenuProps={MenuProps}
						renderValue={(selected) =>
							showValue ? selected.join(', ') : selected.length > 1 ? `${label} (${selected.length})` : selected[0]
						}
						sx={{
							'&:hover': {
								'&& fieldset': {
									border: `1px solid ${color.shades.blue[50]}`,
								},
							},
						}}
					>
						{sortedData?.map((item, i) => (
							<MenuItem
								value={item}
								key={i}
								data-testid={`${testid}-item-${i}`}
								sx={{ height: '25px', padding: '0px 3px' }}
							>
								<Checkbox
									checked={selValue.indexOf(item) > -1}
									size="small"
									style={{
										color: color.primary[90],
									}}
								/>
								{hasTooltip ? (
									<ToolTip tooltipText={item} children={<ListItemText primary={item} />} />
								) : (
									<ListItemText
										primary={item}
										sx={{
											'& .MuiTypography-root': {
												fontSize: '12px',
											},
										}}
									/>
								)}
							</MenuItem>
						))}
					</Select>
				</>
			)}
		</FormControl>
	);
};

const useStyles = makeStyles(() => ({
	dropdown: {
		verticalAlign: 'middle',
		'& .MuiInputLabel-formControl': {
			...themes.textboxlabel,
			color: color.neutral[5],
		},
		'& .Mui-focused.MuiInputLabel-formControl': {
			color: color.signal.info,
		},
		'& .MuiInputBase-input': {
			...themes.bodyRegular,
			color: color.neutral[8],
		},
		'& .MuiSelect-icon': {
			color: color.neutral[5],
		},
		'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
			border: `2px solid ${color.signal.info} !important`,
		},
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
			height: '40px !important',
			paddingTop: '0px',
			paddingBottom: '0px',
			boxSizing: 'border-box',
		},
		// keeps every dropdown variant at the same height so filter rows line up
		'& .MuiOutlinedInput-root': {
			height: '40px',
			boxSizing: 'border-box',
		},
		'& .MuiAutocomplete-root .MuiOutlinedInput-root': {
			padding: '0px 6px',
			height: '40px',
			boxSizing: 'border-box',
			alignItems: 'center',
			flexWrap: 'nowrap',
		},
		'& .MuiAutocomplete-root .MuiAutocomplete-input': {
			paddingTop: '0px',
			paddingBottom: '0px',
		},
		'& ..MuiAutocomplete-inputRoot': {
			minWidth: 20,
		},
		'& .MuiAutocomplete-popper': {
			width: 250,
		},
		'& .MuiInputBase-root': {
			...themes.bodyRegular,
			color: color.neutral[8],
			paddingLeft: '0px !important',
		},
		'& .MuiAutocomplete-popupIndicator': {
			color: color.neutral[5],
		},
	},
}));
